import { Button, Skeleton, TextField } from '@mui/material'
import useReactTable, { Filter } from 'hooks/useReactTable'
import HorizontallyScrolleableElement from 'hooks/useScroll'
import React, { useEffect, useRef, useState } from 'react'
import MUIPagination from '@mui/material/Pagination'
import { translate } from 'utils/propKeysTranslations'
import * as S from './styles'
import AutoScroll from '../Enhancers/AutoScroll'

const Table = ({
  endpoint = '',
  tableData,
  tableDataFetcher,
  paginated = false,
  dataAccessor = endpoint.slice(endpoint.lastIndexOf('/') + 1),
  title = translate(endpoint.slice(endpoint.lastIndexOf('/') + 1)),
  searchOptions = [],
  orderOptions = [],
  aditionalColumns = [],
  paginationOptions: {
    availablePageSizes = [10],
    initialPageSize = availablePageSizes.at(-1),
  } = {},
  skipReferences,
  columnsToSkip,
  columnsToInclude,
  columnsToReplace,
  sseSubscriptions,
  children,
  defaultComponent,
  cellCustomData = {},
  disableAutoScroll = false,
  showDeleted = false,
  loaderWidth,
  urlQueries,
}) => {
  const [orderOptionsState, setOrderOptionsState] = useState({})
  const [searchOptionsState, setSearchOptionsState] = useState({})
  const [tableHeight, setTableHeight] = useState('50vh')
  const [isFirstRender, setIsFirstRender] = useState(true)
  const sectionRef = useRef(null)

  const {
    data,
    fallbackComponent,
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    rows,
    count,
    prepareRow,
    pageIndex,
    pageSize,
    setPageSize,
    gotoPage,
    pageCount,
    setAllFilters,
    filters,
  } = useReactTable({
    endpoint,
    dataAccessor,
    aditionalColumns,
    tableData,
    tableDataFetcher,
    initialPageSize: Math.min(availablePageSizes.at(-1), initialPageSize),
    paginated,
    skipReferences,
    columnsToSkip,
    columnsToInclude,
    columnsToReplace,
    searchOptions: searchOptionsState,
    orderBy: orderOptionsState,
    sseSubscriptions,
    showDeleted,
    urlQueries,
    loaderWidth,
  })

  useEffect(() => {
    if (sectionRef.current) setTableHeight(sectionRef.current.clientHeight)
  }, [data])

  useEffect(() => {
    gotoPage(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchOptionsState])

  useEffect(() => {
    if (data || tableData) setIsFirstRender(false)
  }, [data, tableData])

  const clearFilters = () => {
    setOrderOptionsState({})
    setSearchOptionsState({})
  }

  const paginatedFiltersSetter = (value, id) => {
    if (!value) {
      setSearchOptionsState((ps) => {
        const { [id]: removed, ...rest } = ps
        return rest
      })
    } else {
      setSearchOptionsState((ps) => ({
        ...ps,
        [id]: value,
      }))
    }
  }

  const filtersApplied = !!Object.keys({
    ...orderOptionsState,
    ...searchOptionsState,
  }).length

  const shouldPaginate = paginated && count > availablePageSizes.at(-1)

  if (tableData === null || fallbackComponent) {
    return <Skeleton variant='rounded' width='100%' height={tableHeight} />
  }

  return (
    <AutoScroll
      shouldScroll={
        !disableAutoScroll && isFirstRender && (!!data || !!tableData)
      }
      dependencies={data}
      scrollMargin={count > 0 ? '7vh' : undefined}
    >
      <section
        ref={sectionRef}
        className='w-full max-w-full justify-center my-4 p-5 border-2 border-gray-400 rounded-xl shadow-md text-l'
      >
        <h2>{title}</h2>
        <div className='flex flex-col gap-4 pb-2 border-b-2 border-gray-200'>
          {count > 0 ? (
            <HorizontallyScrolleableElement>
              <S.Table
                {...getTableProps()}
                className='w-full my-2 p-2 pb-6 border-b-4'
              >
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => {
                        const { onClick } = column.getSortByToggleProps()
                        return (
                          <th {...column.getHeaderProps()}>
                            <div className='flex flex-col items-center gap-2 w-max m-auto'>
                              <div className='flex'>
                                {column.render('Header')}
                                {orderOptions.includes(column.id)
                                  && (paginated ? (
                                    <SortPaginated
                                      id={column.id}
                                      setOrderOptions={setOrderOptionsState}
                                      orderOptions={orderOptionsState}
                                    />
                                  ) : (
                                    <Sort column={column} onClick={onClick} />
                                  ))}
                              </div>
                              {searchOptions.includes?.(column.id)
                                && (!paginated ? (
                                  <Filter
                                    key='paginated'
                                    filterValue={column.filterValue}
                                    setFilter={column.setFilter}
                                  />
                                ) : (
                                  <Filter
                                    key='notPaginated'
                                    filterValue={searchOptionsState[column.id]}
                                    setFilter={(value) => {
                                      paginatedFiltersSetter(value, column.id)
                                    }}
                                  />
                                ))}
                            </div>
                          </th>
                        )
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {(shouldPaginate ? page : rows).map((row) => {
                    prepareRow(row)
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => {
                          return (
                            <td {...cell.getCellProps()}>
                              {cell.render('Cell', { cellCustomData })}
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </S.Table>
            </HorizontallyScrolleableElement>
          ) : (
            <p className='text-[length:var(--font-size-large)] text-center self-center'>
              No se encontraron registros de
              {' '}
              {title || translate(dataAccessor)}
            </p>
          )}
          {shouldPaginate && (
            <Pagination
              pageIndex={pageIndex}
              pageCount={pageCount}
              gotoPage={gotoPage}
            />
          )}
          {(paginated ? filtersApplied : !!filters.length) && (
            <Button
              style={{ display: 'block', marginLeft: 'auto' }}
              onClick={paginated ? clearFilters : () => setAllFilters([])}
            >
              Reiniciar filtros
            </Button>
          )}
        </div>
        {children}
      </section>
    </AutoScroll>
  )
}

const Pagination = ({ pageIndex, pageCount, gotoPage }) => {
  const pageIndexForInput = useRef(null)

  useEffect(() => {
    if (pageIndexForInput.current) {
      pageIndexForInput.current.value = pageIndex + 1
    }
  }, [pageIndex])

  const handlePageIndexChange = (e) => {
    e.preventDefault()
    const index = pageIndexForInput.current.value
    let currentPage = parseInt(index, 10)
    if (Number.isNaN(currentPage) || currentPage < 1) currentPage = 1
    else if (currentPage > pageCount) currentPage = pageCount
    handlePageValueChange(currentPage)
  }

  const handlePageValueChange = (pageNumber) => {
    const pageIndex = pageNumber - 1
    pageIndexForInput.current.value = pageNumber
    gotoPage(pageIndex)
  }

  return (
    <S.NavigationContainer>
      <MUIPagination
        className='min-w-max'
        page={pageIndex + 1}
        count={pageCount}
        onChange={(_e, page) => {
          gotoPage(page - 1)
        }}
        showFirstButton
        showLastButton
        siblingCount={2}
      />
      <S.TableOptions>
        <form onSubmit={handlePageIndexChange}>
          <TextField
            size='small'
            variant='filled'
            label='Número de página'
            inputRef={pageIndexForInput}
            sx={{ minWidth: '125px' }}
            fullWidth
          />
        </form>
      </S.TableOptions>
    </S.NavigationContainer>
  )
}

const SortPaginated = ({ id, setOrderOptions, orderOptions }) => (
  <div>
    <button
      onClick={() => {
        setOrderOptions((ps) => {
          const newValues = { ...ps }
          if (ps[id] === undefined) {
            newValues[id] = true
          }
          if (ps[id] === true) {
            newValues[id] = false
          }
          if (ps[id] === false) {
            delete newValues[id]
          }
          return newValues
        })
      }}
      type='button'
    >
      {orderOptions[id] === undefined ? '▶️' : orderOptions[id] ? '🔽' : '🔼'}
    </button>
  </div>
)

const Sort = ({ column, onClick }) => (
  <div>
    <button onClick={onClick} type='button'>
      {column.isSorted ? (column.isSortedDesc ? '🔽' : '🔼') : '▶️'}
    </button>
  </div>
)

const queryObjectEncoder = (o) => {
  if (!Object.keys(o).length) return undefined
  return encodeURIComponent(
    JSON.stringify(o, (_k, v) => (v !== null ? v : undefined)),
  )
}

// const SearchOptions = ({
//   options,
//   searchOptionsState,
//   setSearchOptionsState,
// }) => {
//   const [selectedOption, setSelectedOption] = useState('')
//   const [selectedOptionValue, setSelectedOptionValue] = useState('')
//   const onClickSearchHandler = () => {
//     if (!selectedOption || !selectedOptionValue) return
//     setSearchOptionsState({
//       ...searchOptionsState,
//       [selectedOption]: selectedOptionValue,
//     })
//   }

//   const clearSearch = () => setSearchOptionsState({})
//   const deleteItem = (key) => {
//     const { [key]: removed, ...rest } = searchOptionsState
//     setSearchOptionsState(rest)
//   }
//   return (
//     <div>
//       <S.SearchElements>
//         <TextField
//           value={selectedOption}
//           onChange={(e) => setSelectedOption(e.target.value)}
//           label='Buscar por:'
//           select
//           variant='filled'
//           InputLabelProps={{ sx: { minWidth: '150px' } }}
//           InputProps={{ sx: { height: '100%', minWidth: '150px' } }}
//         >
//           {options.map((size) => (
//             <MenuItem key={size} value={size}>
//               {translate(size)}
//             </MenuItem>
//           ))}
//         </TextField>
//         {selectedOption && (
//           <>
//             <SearchOptionInput
//               selectedOptionValue={selectedOptionValue}
//               setSelectedOptionValue={setSelectedOptionValue}
//             />
//             <Button
//               disabled={!selectedOptionValue}
//               onClick={onClickSearchHandler}
//
//             >
//               Buscar
//             </Button>
//           </>
//         )}
//         {!!Object.keys(searchOptionsState).length && (
//           <Button
//             disabled={!selectedOptionValue}
//             onClick={clearSearch}
//
//           >
//             Borrar palabras clave
//           </Button>
//         )}
//       </S.SearchElements>
//       <div className='flex gap-2 my-4'>
//         {Object.entries(searchOptionsState).map(([k, v]) => (
//           <Chip
//             label={`${translate(k)}: " ${v} "`}
//             key={k}
//             onDelete={() => deleteItem(k)}
//             style={{ fontWeight: 700 }}
//           />
//         ))}
//       </div>
//     </div>
//   )
// }

// const SearchOptionInput = ({ selectedOptionValue, setSelectedOptionValue }) => {
//   return (
//     <TextField
//       value={selectedOptionValue}
//       onChange={(e) => setSelectedOptionValue(e.target.value)}
//       label='Palabra clave'
//       variant='filled'
//       InputProps={{ sx: { height: '100%' } }}
//     />
//   )
// }

const checkEquality = (obj1, obj2) => {
  const keyValuePairs1 = Object.entries(obj1).sort(([a], [b]) => a > b)
  const keyValuePairs2 = Object.entries(obj2).sort(([a], [b]) => a > b)
  if (keyValuePairs1.length !== keyValuePairs2.length) return false
  for (let i = 0; i < keyValuePairs1.length; i++) {
    const k1 = keyValuePairs1[i][0]
    const k2 = keyValuePairs2[i][0]
    const v1 = keyValuePairs1[i][1]
    const v2 = keyValuePairs2[i][1]
    if (k1 !== k2 || v1 !== v2) return false
  }
  return true
}

const getRandomColor = () =>
  `#${((Math.random() * 0xffffff) << 0).toString(16)}`

export default Table
